import type { FirebaseApp } from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";

// App Check (#126): attest that Firebase calls (Auth sign-up/in, Firestore,
// Functions) come from our genuine web app, so scripted bot sign-ups are
// rejected once enforcement is turned on in the Firebase console. reCAPTCHA is
// invisible — there is no user-facing challenge.
//
// Uses reCAPTCHA Enterprise: Firebase's App Check console no longer offers the
// classic reCAPTCHA v3 provider for new registrations (it's deprecated), so the
// site key registered in App Check is an Enterprise key and must be paired with
// ReCaptchaEnterpriseProvider here.
//
// This is the web implementation; iOS/Android attest with App Attest / Play
// Integrity through the native SDK instead — see appCheck.native.ts.
export function initAppCheck(app: FirebaseApp): void {
  // Only meaningful in a browser; guards SSR/prerender and non-web callers.
  if (typeof window === "undefined") return;

  // reCAPTCHA site keys are public — they ship in the client — so reading the
  // key from a public env var (inlined by Expo at build time) is fine. Without a
  // key we skip init rather than crash, so a misconfigured build still runs.
  const siteKey = process.env.EXPO_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY;
  if (!siteKey) {
    console.warn(
      "App Check: EXPO_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY is not set — skipping App Check init.",
    );
    return;
  }

  // Local dev (http://localhost) can't solve reCAPTCHA, so App Check would hand
  // out no token and — once enforcement is on — every request would 401. A debug
  // token registered in the Firebase console lets the dev browser get valid
  // tokens. Set EXPO_PUBLIC_APPCHECK_DEBUG_TOKEN to that token (or to "true" once
  // to have the SDK print a fresh one in the console to register).
  const debugToken = process.env.EXPO_PUBLIC_APPCHECK_DEBUG_TOKEN;
  if (debugToken) {
    (
      self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string }
    ).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken === "true" ? true : debugToken;
  }

  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      // Refresh the App Check token in the background so long sessions keep a
      // valid token without a page reload.
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    // Non-fatal: the app still runs; requests are simply unattested until this
    // is fixed. (Won't be blocked unless/until enforcement is enabled.)
    console.warn("App Check: failed to initialize", err);
  }
}
